import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AirlinerTwoFactorAuthPage = () => {
  const { post, pass } = useParams();
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [method, setMethod] = useState('email'); // 'email' or 'sms'

  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    if (error) setError('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    const code = verificationCode.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate verification (accept 123456 as valid for demo)
      if (code === '123456') {
        alert('Two-factor authentication successful!');
        navigate('/airliner');
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert(`Verification code sent via ${method === 'email' ? 'email' : 'SMS'}!`);
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const switchMethod = () => {
    setMethod(method === 'email' ? 'sms' : 'email');
    setVerificationCode(['', '', '', '', '', '']);
    setError('');
  };

  const lockIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
  );

  return (
    <AuthLayout
      title="Two-Factor Authentication"
      subtitle={method === 'email' 
        ? 'Enter the 6-digit code sent to your email address'
        : 'Enter the 6-digit code sent to your mobile phone'
      }
      icon={lockIcon}
      iconBgColor="bg-jet-yellow-100"
      iconColor="text-jet-yellow-600"
      showDemo={true}
      demoText="Use code 123456 to verify"
    >
      <form onSubmit={handleVerification} className="space-y-6">
        {/* Verification Code Input */}
        <div>
          <label className="block text-sm font-medium text-jet-gray-700 mb-3 text-center">
            Verification Code
          </label>
          <div className="flex justify-center space-x-3 mb-4">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-colors ${
                  error 
                    ? 'border-jet-red-400 focus:border-jet-red-500' 
                    : 'border-jet-gray-300 focus:border-jet-yellow'
                }`}
              />
            ))}
          </div>
          
          {error && (
            <p className="text-sm text-jet-red-600 text-center mb-4">{error}</p>
          )}
        </div>

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          disabled={verificationCode.join('').length !== 6}
          loadingText="Verifying..."
          className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900"
        >
          Verify Code
        </LoadingButton>

        {/* Resend Code */}
        <div className="text-center space-y-3">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading || resendCooldown > 0}
            className={`text-sm transition-colors ${
              resendLoading || resendCooldown > 0
                ? 'text-jet-gray-400 cursor-not-allowed'
                : 'text-jet-yellow-600 hover:text-jet-yellow-700'
            }`}
          >
            {resendLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jet-gray-400 mr-2"></div>
                Sending...
              </div>
            ) : resendCooldown > 0 ? (
              `Resend code in ${resendCooldown}s`
            ) : (
              `Resend code via ${method === 'email' ? 'email' : 'SMS'}`
            )}
          </button>

          {/* Switch Method */}
          <div className="text-sm text-jet-gray-600">
            Didn't receive the code?{' '}
            <button
              type="button"
              onClick={switchMethod}
              className="text-jet-yellow-600 hover:text-jet-yellow-700 font-medium"
            >
              Try {method === 'email' ? 'SMS' : 'email'} instead
            </button>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/airliner/auth/login')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            Back to Login
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AirlinerTwoFactorAuthPage;